import Lockup from '@/modules/Celebrating20YearsPage/components/Lockup';
import { ASSETS, MILESTONES, MilestoneItem } from '@/modules/Celebrating20YearsPage/content';
import cx from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import styles from './Milestones.module.scss';

const MilestoneTileContent = ({ item }: { item: MilestoneItem }) => {
  switch (item.tile) {
    case 'medrecruit':
      return (
        <span className={styles.logo} role="img" aria-label="Medrecruit logo">
          <Image src={`${ASSETS}/medrecruit-mark.svg`} alt="" width={32} height={33} />
          <Image
            className={styles.logoWordmark}
            src={`${ASSETS}/medrecruit-wordmark.svg`}
            alt=""
            width={131}
            height={20}
          />
        </span>
      );
    case 'medworld':
      return (
        <span className={styles.logo} role="img" aria-label="Medworld logo">
          <Image src={`${ASSETS}/medworld-mark.svg`} alt="" width={33} height={35} />
          <Image
            className={styles.logoWordmark}
            src={`${ASSETS}/medworld-wordmark.svg`}
            alt=""
            width={129}
            height={21}
          />
        </span>
      );
    case 'deloitte':
      return (
        <Image
          className={styles.deloitte}
          src={`${ASSETS}/deloitte-fast-50.png`}
          alt="Deloitte Technology Fast 50 logo"
          width={150}
          height={150}
        />
      );
    case 'bcorp':
      return (
        <span className={styles.bcorp} role="img" aria-label="Certified B Corporation logo">
          <Image src={`${ASSETS}/bcorp-1.svg`} alt="" width={51} height={52} />
          <Image src={`${ASSETS}/bcorp-2.svg`} alt="" width={51} height={9} />
          <Image src={`${ASSETS}/bcorp-3.svg`} alt="" width={51} height={10} />
          <Image src={`${ASSETS}/bcorp-4.svg`} alt="" width={51} height={8} />
        </span>
      );
    case 'medworldInstitute':
      return (
        <Image
          src={`${ASSETS}/medworld-institute.svg`}
          alt="Medworld Institute logo"
          width={165}
          height={45}
        />
      );
    case 'lockup':
      return <Lockup size="small" />;
    case 'photo':
    case 'photoFlipped':
      return item.image ? (
        <Image
          src={item.image.src}
          alt={item.image.alt}
          width={item.image.width}
          height={item.image.height}
          sizes="(max-width: 767px) 100vw, 217px"
        />
      ) : null;
  }
};

const isExternal = (url: string) => /^https?:\/\//.test(url);

const tileClassName: Record<MilestoneItem['tile'], string | undefined> = {
  medrecruit: undefined,
  photo: styles.tilePhoto,
  photoFlipped: cx(styles.tilePhoto, styles.tileFlip),
  deloitte: undefined,
  medworld: styles.tileLight,
  bcorp: styles.tileShade,
  medworldInstitute: styles.tileMwi,
  lockup: styles.tileLight,
};

const Milestones = () => {
  const lineRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  // Content renders fully visible server-side; the animation is only switched on once JS runs
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!reducedMotion.matches && 'IntersectionObserver' in window) {
      setAnimate(true);
    }
  }, []);

  useEffect(() => {
    const line = lineRef.current;
    const fill = fillRef.current;
    const list = listRef.current;
    if (!animate || !line || !fill || !list) {
      return;
    }

    const reveals = list.querySelectorAll(`.${styles.reveal}`);
    const dots = Array.from(list.querySelectorAll<HTMLElement>(`.${styles.dot}`));

    // Fade/slide each year and card in once
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            return;
          }
          entry.target.classList.add(styles.isVisible);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );
    reveals.forEach(item => observer.observe(item));

    // Progress fill driven by the timeline's position in the viewport
    let frame: number | undefined;
    const update = () => {
      frame = undefined;
      const lineRect = line.getBoundingClientRect();
      const trigger = window.innerHeight * 0.6;
      const progress = Math.min(1, Math.max(0, (trigger - lineRect.top) / lineRect.height));
      fill.style.transform = `scaleY(${progress})`;

      const filledTo = lineRect.top + lineRect.height * progress;
      dots.forEach(dot => {
        const rect = dot.getBoundingClientRect();
        dot.classList.toggle(styles.isActive, rect.top + rect.height / 2 <= filledTo);
      });
    };
    const requestUpdate = () => {
      if (frame === undefined) {
        frame = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [animate]);

  return (
    <section className={styles.milestones} aria-labelledby="milestones-title">
      <div className={styles.container}>
        <div className={styles.sectionHead}>
          <h2 id="milestones-title">
            <span className={styles.highlight}>20 years</span> in milestones
          </h2>
          <p className={styles.sectionSub}>Our journey from 2006</p>
        </div>

        <div className={cx(styles.wrap, animate && styles.animate)}>
          <div className={styles.line} ref={lineRef} aria-hidden="true">
            <span className={styles.fill} ref={fillRef} />
          </div>

          <ol className={styles.timeline} ref={listRef}>
            {MILESTONES.map(milestone => (
              <li className={styles.entry} key={milestone.year}>
                <p className={cx(styles.year, styles.reveal)}>
                  <time dateTime={milestone.year}>{milestone.year}</time>
                </p>
                <span className={styles.dot} aria-hidden="true" />
                <div
                  className={cx(styles.card, styles.reveal, {
                    [styles.cardCompact]: milestone.compact,
                    [styles.cardSingle]: milestone.items.length === 1,
                  })}
                >
                  {milestone.items.map(item => (
                    <div className={styles.item} key={item.title}>
                      <figure className={cx(styles.tile, tileClassName[item.tile])}>
                        <MilestoneTileContent item={item} />
                      </figure>
                      <div className={styles.body}>
                        <h3 className={styles.title}>{item.title}</h3>
                        {item.description && <p className={styles.desc}>{item.description}</p>}
                        {item.readMoreUrl && (
                          <Link
                            className={styles.readMore}
                            href={item.readMoreUrl}
                            {...(isExternal(item.readMoreUrl) && {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            })}
                          >
                            Read more<span className={styles.srOnly}> about {item.title}</span>
                            <Image
                              src={`${ASSETS}/arrow-right.svg`}
                              alt=""
                              width={24}
                              height={24}
                            />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default Milestones;
